import { useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import * as zod from 'zod';
import { LggPopover } from 'src/components/general/display/popover';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { preventDefaultAndStopPropagation } from 'src/components/general/inputs/helpers';
import { TextInput } from 'src/components/general/inputs/text-input';
import {
  PlaceholderOptionsOverlay,
  TemplatePlaceholderOverlayBody,
  TemplatePlaceholderOverlayContainer,
  TemplatePlaceholderOverlayFooter,
  TemplatePlaceholderOverlayHeader,
} from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';

type TemplateCustomPlaceholderProps = {
  children: React.ReactChild;
  label: string;
  description?: string;
  onSubmit: (label: string, description?: string) => void;
  onRemove: VoidFunction;
};

export const TemplateCustomPlaceholder = ({
  children,
  onSubmit,
  onRemove,
  label,
  description,
}: TemplateCustomPlaceholderProps) => {
  const popoverVisibility = useVisible();

  return (
    <span>
      <PlaceholderOptionsOverlay
        onEdit={() => {
          popoverVisibility.show();
        }}
        onRemove={onRemove}
      >
        {children}
      </PlaceholderOptionsOverlay>
      <TemplateCustomPlaceholderPopover
        onSubmit={onSubmit}
        visible={popoverVisibility.visible}
        description={description}
        label={label}
        onClose={() => {
          popoverVisibility.close();
        }}
      />
    </span>
  );
};

const InputContainer = styled.div`
  & > *:first-child {
    margin-bottom: 10px !important;
  }

  ${up('md')} {
    padding: 5px;

    & > *:first-child {
      margin-bottom: 25px !important;
    }
  }
`;

const placeholderFormSchema = zod.object({
  label: zod.string().min(1),
  description: zod.string().nullish(),
});

type FallbackFormValues = zod.infer<typeof placeholderFormSchema>;

const defaultValues: Partial<FallbackFormValues> = {
  label: undefined,
  description: null,
};

type TemplateCustomPlaceholderOverlayProps = {
  visible: boolean;
  onClose: VoidFunction;
  label?: string;
  description?: string;
  onSubmit: (label: string, description?: string) => void;
};

export const TemplateCustomPlaceholderPopover = ({
  description,
  label,
  onSubmit,
  visible,
  onClose,
}: TemplateCustomPlaceholderOverlayProps) => {
  const { t } = useTranslation(['conversations']);
  const breakpointUpMd = useBreakpoint(up('md'));

  const form = useForm<FallbackFormValues>({
    resolver: zodResolver(placeholderFormSchema),
    defaultValues: {
      label: label ?? defaultValues.label,
      description: description ?? defaultValues.description,
    },
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!visible) {
      form.reset();
    }
  }, [form, visible]);

  const onSubmitHandler = useCallback(async () => {
    await form.handleSubmit(({ label, description }) => {
      onSubmit(label, description ?? '');
      onClose();
    })();
  }, [form, onClose, onSubmit]);

  const formContent = useMemo(() => {
    return (
      <FormProvider {...form}>
        <form
          onSubmit={async (e) => {
            preventDefaultAndStopPropagation(e);

            await onSubmitHandler();
          }}
        >
          <TemplatePlaceholderOverlayBody data-lgg-id="create-template-custom-placeholder-overlay">
            <InputContainer>
              <Controller
                control={form.control}
                name="label"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      label={t(
                        'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.customPlaceholder.createPopover.inputs.label.label',
                      )}
                      placeholder={t(
                        'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.customPlaceholder.createPopover.inputs.label.placeholder',
                      )}
                      data-lgg-id="create-template-custom-placeholder-overlay-label-field"
                      autoComplete="off"
                      error={Boolean(fieldState.error)}
                      showErrorMessage={false}
                    />
                  );
                }}
              />
              <Controller
                control={form.control}
                name="description"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      value={field.value ?? ''}
                      label={t(
                        'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.customPlaceholder.createPopover.inputs.description.label',
                      )}
                      placeholder={t(
                        'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.customPlaceholder.createPopover.inputs.description.placeholder',
                      )}
                      autoComplete="off"
                      data-lgg-id="create-template-custom-placeholder-overlay-description-field"
                      error={Boolean(fieldState.error)}
                      showErrorMessage={false}
                    />
                  );
                }}
              />
            </InputContainer>
          </TemplatePlaceholderOverlayBody>
          <TemplatePlaceholderOverlayFooter onClose={onClose} />
        </form>
      </FormProvider>
    );
  }, [form, onClose, onSubmitHandler, t]);

  return breakpointUpMd ? (
    <LggPopover
      content={
        <TemplatePlaceholderOverlayContainer>
          <TemplatePlaceholderOverlayHeader
            title={t(
              'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.customPlaceholder.createPopover.title',
            )}
          />
          {formContent}
        </TemplatePlaceholderOverlayContainer>
      }
      hideArrow
      placement="topLeft"
      trigger={['click']}
      destroyTooltipOnHide
      visible={visible}
      onVisibleChange={() => {
        onClose();
      }}
    >
      <span></span>
    </LggPopover>
  ) : (
    <BottomDrawer
      visible={visible}
      destroyOnClose
      title={t(
        'conversations:messageInput.options.messageTemplates.editPlaceholderOverlay.titleShort',
      )}
      onClose={onClose}
    >
      {formContent}
    </BottomDrawer>
  );
};
